/**
 * Pure schedule <-> dialog-state mapping for the Repeat/Recurrence editor.
 * Split out of RecurrenceDialog.jsx so it carries no JSX and can be unit-tested
 * with `node --test`.
 *
 * KEY RULE — "Weekdays" means EVERY WORKING DAY, not a hardcoded Mon–Fri. It
 * emits `{ freq: 'daily' }`; the backend daily engine (computeNextRunAt) skips
 * non-working days using the tenant's Report Settings working week
 * (`dailyActiveDays`). So a six-day company (Mon–Sat) repeats Mon–Sat and a
 * five-day one repeats Mon–Fri — the two features stay in step. A fixed
 * `byWeekday: [1..5]` list here would ignore Report Settings and wrongly skip a
 * configured Saturday.
 */

/** Mon–Fri in JS getDay() numbering (0=Sun..6=Sat). Kept only to RECOGNISE
 *  legacy rules that were saved as weekly Mon–Fri before this change. */
export const WEEKDAYS_MON_FRI = [1, 2, 3, 4, 5]

const FULL_DAY_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** Full weekday names for the chosen `byWeekday` values, in Sun→Sat order,
 *  joined naturally ("Monday, Wednesday and Friday"). */
export function dayList(weekdays) {
  const names = [...new Set(weekdays ?? [])]
    .filter((v) => v >= 0 && v <= 6)
    .sort((a, b) => a - b)
    .map((v) => FULL_DAY_NAME[v])
  if (names.length === 0) return 'the selected days'
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** A plain-English sentence describing the current selection — the friendly
 *  translation of the terse preset labels. */
export function describeSchedule(st) {
  const n = Math.max(1, Number(st.interval) || 1)
  switch (st.preset) {
    case 'daily':
      return 'Repeats every working day — weekends and other days off set in Report Settings are skipped.'
    case 'weekdays':
      return 'Repeats every working day — follows your company’s working week (set in Report Settings); days off are skipped.'
    case 'weekly':
      return `Repeats every week on ${dayList(st.weekdays)}.`
    case 'biweekly':
      return `Repeats once every 2 weeks on ${dayList(st.weekdays)}.`
    case 'monthly':
      return 'Repeats on the last day of every month.'
    case 'custom':
    default:
      if (st.freq === 'weekly')
        return `Repeats every ${n} week${n === 1 ? '' : 's'} on ${dayList(st.weekdays)}.`
      if (st.freq === 'monthly')
        return `Repeats on the last day of every ${n} month${n === 1 ? '' : 's'}.`
      return `Repeats every ${n} day${n === 1 ? '' : 's'}.`
  }
}

const SHORT_DAY_NAME = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A COMPACT summary for the list's Repeat column — "Weekly · Mon, Wed, Fri".
 *
 *  `describeSchedule` returns a full sentence, which is right inside the Repeat
 *  dialog but far too long for a table cell; the list could therefore only show
 *  a generic "Recurring" badge, so the user had to open Edit to see which days
 *  the series ran on (defect DF_006 / Mantis 22086). Use the sentence as the
 *  cell's `title` and this as its text.
 *
 *  Takes a RAW schedule ({ freq, interval, byWeekday, byMonthDay }) — the shape
 *  the task list API now returns as `recurringSchedule`. Returns null when
 *  there is nothing to describe, so the caller can fall back to its badge.
 *
 *  Note there is no time-of-day: recurrence has no time setting — every
 *  occurrence is generated at the start of its day. */
export function summariseSchedule(schedule) {
  if (!schedule || !schedule.freq) return null
  const n = Math.max(1, Number(schedule.interval) || 1)
  const days = [...new Set(schedule.byWeekday ?? [])]
    .filter((v) => v >= 0 && v <= 6)
    .sort((a, b) => a - b)
    .map((v) => SHORT_DAY_NAME[v])
    .join(', ')
  switch (schedule.freq) {
    case 'daily':
      // interval 1 is the "every working day" preset — the engine skips the
      // company's non-working days, so say so rather than implying all 7.
      return n === 1 ? 'Every working day' : `Every ${n} days`
    case 'weekly': {
      const every = n === 1 ? 'Weekly' : n === 2 ? 'Every 2 weeks' : `Every ${n} weeks`
      return days ? `${every} · ${days}` : every
    }
    case 'monthly':
      return n === 1 ? 'Monthly · last day' : `Every ${n} months · last day`
    case 'cron':
      return 'Custom schedule'
    default:
      return null
  }
}

/** Reverse-map an existing rule's schedule → the dialog's preset + fields. */
export function scheduleToState(rule) {
  // New rules default to Weekdays = every WORKING day (`daily`, interval 1). The
  // daily engine skips non-working days using the tenant's Report Settings
  // working week, so this stays in step with the company's working days (e.g.
  // Mon–Sat) instead of a hardcoded Mon–Fri.
  const s = rule?.schedule ?? { freq: 'daily', interval: 1 }
  const interval = s.interval ?? 1
  const byWeekday = Array.isArray(s.byWeekday) ? s.byWeekday : []
  let preset = 'custom'
  if (s.freq === 'daily' && interval === 1) preset = 'weekdays'
  // Legacy rules saved as weekly Mon–Fri still show under the Weekdays chip so
  // they don't look broken (they keep firing Mon–Fri until re-saved).
  else if (
    s.freq === 'weekly' &&
    interval === 1 &&
    byWeekday.length === 5 &&
    WEEKDAYS_MON_FRI.every((d) => byWeekday.includes(d))
  )
    preset = 'weekdays'
  else if (s.freq === 'weekly' && interval === 1) preset = 'weekly'
  else if (s.freq === 'weekly' && interval === 2) preset = 'biweekly'
  else if (s.freq === 'monthly' && interval === 1) preset = 'monthly'
  return {
    preset,
    interval,
    weekdays: byWeekday.length ? byWeekday : [new Date().getDay()],
    monthDay: s.byMonthDay ?? new Date().getDate(),
    freq: s.freq === 'cron' ? 'daily' : s.freq,
  }
}

/** Build the backend schedule payload from the dialog state. */
export function stateToSchedule(st) {
  switch (st.preset) {
    case 'daily':
      return { freq: 'daily', interval: 1 }
    case 'weekdays':
      // "Every working day" — see the module header. The daily engine applies
      // the tenant's Report Settings working week, so this fires Mon–Sat for a
      // six-day company and Mon–Fri for a five-day one.
      return { freq: 'daily', interval: 1 }
    case 'weekly':
      return { freq: 'weekly', interval: 1, byWeekday: st.weekdays }
    case 'biweekly':
      return { freq: 'weekly', interval: 2, byWeekday: st.weekdays }
    case 'monthly':
      // 31 = "last day of month" sentinel (engine fires month-end regardless),
      // mirroring the Report Settings convention. No per-day choice.
      return { freq: 'monthly', interval: 1, byMonthDay: 31 }
    case 'custom':
    default:
      return st.freq === 'weekly'
        ? { freq: 'weekly', interval: st.interval, byWeekday: st.weekdays }
        : st.freq === 'monthly'
          ? { freq: 'monthly', interval: st.interval, byMonthDay: 31 }
          : { freq: 'daily', interval: st.interval }
  }
}
